import { Injectable, computed, Signal, inject } from '@angular/core';
import { Load } from '../models/load.model';
import { LoadStatusType } from '../enums/load-status.enum';
import { PermissionsService } from './permissions.service';
import { LoadValidationService } from './load-validation.service';

export interface LoadDetailsComponentState {
  mainActionText: string;
  mainActionIcon: string;
  mainActionHandler: () => void;
  canUpdateLoadStatus: boolean;
  canEditBookingDetails: boolean;
  canEditAssetAssignments: boolean;
  canEditDetails: boolean;
  canEditNotes: boolean;
  canEditStops: boolean;
  canEditRoute: boolean;
  canCancelLoad: boolean;
  canReviveLoad: boolean;
  canAddHandoff: boolean;
  showGenerateDispatchButton: boolean;
  canGenerateDispatchSheet: boolean;
  showMainAction: boolean;
  warnOnEdit: boolean; // Show confirmation dialog for edits in sensitive states
  mainActionDisabled?: boolean;
  mainActionTooltip?: string;
}

@Injectable({
  providedIn: 'root',
})
export class LoadStateService {
  private readonly permissions = inject(PermissionsService);
  private readonly loadValidationService = inject(LoadValidationService);
  /**
   * Computes the complete state for a load based on its status and properties
   */
  computeLoadState(
    load: Signal<Load | null>,
    hasDispatchSheet: Signal<boolean>,
    handlers: {
      onMarkVerified: () => void;
      onAssignAssets: () => void;
      onGenerateDispatch: () => void;
      onProvideETA: () => void;
      onStatusUpdate: () => void;
      onResolveActionRequired?: () => void;
    }
  ): Signal<LoadDetailsComponentState> {
    return computed(() => {
      const currentLoad = load();

      if (!currentLoad) {
        return this.getDefaultState();
      }


      const status = currentLoad.status;
      const hasSheet = hasDispatchSheet();
      const canEditElevated = this.permissions.canEditConfirmedLoads();

      switch (status) {
        case 'DRAFT':
          return this.getDraftState(currentLoad, handlers.onMarkVerified);

        case 'VERIFIED':
          return this.getVerifiedState(currentLoad, handlers.onAssignAssets);

        case 'DISPATCHED':
          return this.getDispatchedState(
            hasSheet,
            hasSheet ? handlers.onProvideETA : handlers.onGenerateDispatch
          );

        case 'ACTIVE':
        case 'IN_TRANSIT':
        case 'AT_STOP':
          return this.getActiveState(handlers.onStatusUpdate, canEditElevated);

        case 'DELIVERED':
          return this.getDeliveredState(canEditElevated);

        case 'CANCELLED':
          return this.getCancelledState();

        case 'ACTION_REQUIRED':
          // Fallback if status itself is ACTION_REQUIRED (legacy support)
          return this.getActionRequiredState(
            currentLoad.lastStatus || 'ACTIVE',
            handlers.onResolveActionRequired || (() => {}),
            canEditElevated
          );

        default:
          return this.getDefaultState();
      }
    });
  }

  /**
   * Check if load has assets assigned
   */
  private hasAssetsAssigned(load: Load): boolean {
    return !!(
      (load.driver1Id || load.driver2Id) &&
      load.truckId &&
      load.trailerId
    );
  }

  /**
   * DRAFT state configuration
   */
  private getDraftState(load: Load, handler: () => void): LoadDetailsComponentState {
    const validation = this.loadValidationService.validateDraftLoad(load);

    return {
      mainActionText: 'Verify & Confirm',
      mainActionIcon: 'ShieldCheck',
      mainActionHandler: () => {
        if (!validation.isValid) {
          return;
        }
        handler();
      },
      canUpdateLoadStatus: true,
      canEditBookingDetails: true,
      canEditAssetAssignments: true,
      canEditDetails: true,
      canEditNotes: true,
      canEditStops: true,
      canEditRoute: true,
      canCancelLoad: true,
      canReviveLoad: false,
      canAddHandoff: false,
      showGenerateDispatchButton: false,
      canGenerateDispatchSheet: false,
      showMainAction: true,
      warnOnEdit: false,
      mainActionDisabled: !validation.isValid,
      mainActionTooltip: !validation.isValid ? validation.firstErrorMessage : undefined,
    };
  }

  /**
   * VERIFIED state configuration (was READY)
   */
  private getVerifiedState(load: Load, handler: () => void): LoadDetailsComponentState {
    return {
      mainActionText: 'Assign Driver & Equipment',
      mainActionIcon: 'Users',
      mainActionHandler: handler,
      canUpdateLoadStatus: true,
      canEditBookingDetails: true,
      canEditAssetAssignments: true,
      canEditDetails: true,
      canEditNotes: true,
      canEditStops: true,
      canEditRoute: true,
      canCancelLoad: true,
      canReviveLoad: false,
      canAddHandoff: this.hasAssetsAssigned(load),
      showGenerateDispatchButton: false,
      canGenerateDispatchSheet: false,
      showMainAction: true,
      warnOnEdit: false,
    };
  }

  /**
   * DISPATCHED state configuration (was ASSIGNED)
   */
  private getDispatchedState(
    hasDispatchSheet: boolean,
    handler: () => void
  ): LoadDetailsComponentState {
    return {
      mainActionText: hasDispatchSheet
        ? 'Provide ETA to Pickup'
        : 'Generate Dispatch Sheet',
      mainActionIcon: hasDispatchSheet ? 'Clock' : 'FileText',
      mainActionHandler: handler,
      canUpdateLoadStatus: true,
      canEditBookingDetails: true,
      canEditAssetAssignments: true,
      canEditDetails: true,
      canEditNotes: true,
      canEditStops: true,
      canEditRoute: true,
      canCancelLoad: true,
      canReviveLoad: false,
      canAddHandoff: true,
      showGenerateDispatchButton: true,
      canGenerateDispatchSheet: true,
      showMainAction: true,
      warnOnEdit: true, // Warn: driver already has this load info
    };
  }

  /**
   * ACTIVE state configuration (and related in-transit states)
   */
  private getActiveState(handler: () => void, canEditElevated = false): LoadDetailsComponentState {
    return {
      mainActionText: 'Provide Status Update',
      mainActionIcon: 'MapPin',
      mainActionHandler: handler,
      canUpdateLoadStatus: true,
      canEditBookingDetails: canEditElevated,
      canEditAssetAssignments: canEditElevated,
      canEditDetails: canEditElevated,
      canEditNotes: true,
      canEditStops: true,
      canEditRoute: true,
      canCancelLoad: true,
      canReviveLoad: false,
      canAddHandoff: true,
      showGenerateDispatchButton: true,
      canGenerateDispatchSheet: true,
      showMainAction: true,
      warnOnEdit: true,
    };
  }

  /**
   * ACTION_REQUIRED overlay state
   * Preserves the underlying status context while showing a "Resolve Issue" action.
   */
  private getActionRequiredState(
    underlyingStatus: LoadStatusType,
    handler: () => void,
    canEditElevated = false
  ): LoadDetailsComponentState {
    return {
      mainActionText: 'Resolve Issue',
      mainActionIcon: 'AlertTriangle',
      mainActionHandler: handler,
      canUpdateLoadStatus: true,
      canEditBookingDetails: canEditElevated,
      canEditAssetAssignments: canEditElevated,
      canEditDetails: canEditElevated,
      canEditNotes: true,
      canEditStops: true,
      canEditRoute: true,
      canCancelLoad: true,
      canReviveLoad: false,
      canAddHandoff: true,
      showGenerateDispatchButton: true,
      canGenerateDispatchSheet: true,
      showMainAction: true,
      warnOnEdit: true,
    };
  }

  /**
   * DELIVERED state configuration
   */
  private getDeliveredState(canEditElevated = false): LoadDetailsComponentState {
    return {
      mainActionText: 'Load Delivered',
      mainActionIcon: 'CircleCheckBig',
      mainActionHandler: () => {},
      canUpdateLoadStatus: false,
      canEditBookingDetails: canEditElevated,
      canEditAssetAssignments: canEditElevated,
      canEditDetails: canEditElevated,
      canEditNotes: true,
      canEditStops: true,
      canEditRoute: true,
      canCancelLoad: false,
      canReviveLoad: false,
      canAddHandoff: canEditElevated,
      showGenerateDispatchButton: true,
      canGenerateDispatchSheet: true,
      showMainAction: false,
      warnOnEdit: true,
    };
  }

  /**
   * CANCELLED state configuration
   */
  private getCancelledState(): LoadDetailsComponentState {
    return {
      mainActionText: 'Load Cancelled',
      mainActionIcon: 'XCircle',
      mainActionHandler: () => {},
      canUpdateLoadStatus: false,
      canEditBookingDetails: false,
      canEditAssetAssignments: false,
      canEditDetails: false,
      canEditNotes: false,
      canEditStops: false,
      canEditRoute: false,
      canCancelLoad: false,
      canReviveLoad: true,
      canAddHandoff: false,
      showGenerateDispatchButton: false,
      canGenerateDispatchSheet: false,
      showMainAction: false,
      warnOnEdit: false,
    };
  }

  /**
   * Default/fallback state
   */
  private getDefaultState(): LoadDetailsComponentState {
    return {
      mainActionText: '',
      mainActionIcon: '',
      mainActionHandler: () => {},
      canUpdateLoadStatus: false,
      canEditBookingDetails: false,
      canEditAssetAssignments: false,
      canEditDetails: false,
      canEditNotes: false,
      canEditStops: false,
      canEditRoute: false,
      canCancelLoad: false,
      canReviveLoad: false,
      canAddHandoff: false,
      showGenerateDispatchButton: false,
      canGenerateDispatchSheet: false,
      showMainAction: false,
      warnOnEdit: false,
    };
  }
}
